/**
 * @component InputFile
 * @description shadcn/ui 기반 컴포넌트. CSS 변수를 활용한 테마 시스템 적용.
 * @based-on shadcn/ui + Figma design specification
 */

import { forwardRef, useRef, useState } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../lib/utils';
import { Upload, File, X } from 'lucide-react';
import type { InputHTMLAttributes } from 'react';

export type InputFileSize = 'small' | 'regular' | 'large';

export interface InputFileProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size' | 'type' | 'onChange'> {
  size?: InputFileSize;
  accept?: string;
  maxSize?: number;
  onFileSelect?: (files: FileList | null) => void;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
}

const inputFileVariants = cva(
  'relative flex items-center justify-center border-2 border-dashed rounded-lg transition-colors cursor-pointer hover:border-primary hover:bg-accent-0',
  {
    variants: {
      size: {
        small: 'p-4',
        regular: 'p-6',
        large: 'p-8',
      },
      error: {
        true: 'border-danger bg-red-50',
        false: 'border-border bg-card',
      },
      hasFile: {
        true: 'border-primary bg-blue-50',
        false: '',
      },
    },
    defaultVariants: {
      size: 'regular',
      error: false,
      hasFile: false,
    },
  }
);

export const InputFile = forwardRef<HTMLInputElement, InputFileProps>(
  ({ className, size, accept, maxSize, onFileSelect, error, helperText, disabled, ...props }, ref) => {
    const innerRef = useRef<HTMLInputElement>(null);
    const [fileName, setFileName] = useState<string | null>(null);

    const handleClick = () => {
      if (!disabled) {
        innerRef.current?.click();
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const files = e.target.files;
      if (files && files.length > 0) {
        setFileName(files[0].name);
        onFileSelect?.(files);
      }
    };

    const handleClear = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (innerRef.current) {
        innerRef.current.value = '';
      }
      setFileName(null);
      onFileSelect?.(null);
    };

    return (
      <div className="space-y-1.5">
        <div
          onClick={handleClick}
          className={cn(
            inputFileVariants({ size, error, hasFile: !!fileName }),
            disabled && 'opacity-50 cursor-not-allowed',
            className
          )}
        >
          <input
            ref={(node) => {
              (innerRef as React.MutableRefObject<HTMLInputElement | null>).current = node;
              if (typeof ref === 'function') ref(node);
              else if (ref) ref.current = node;
            }}
            type="file"
            accept={accept}
            onChange={handleChange}
            disabled={disabled}
            className="sr-only"
            {...props}
          />

          {fileName ? (
            <div className="flex items-center gap-2 text-sm text-foreground">
              <File className="w-5 h-5 text-primary" />
              <span className="truncate max-w-[200px]">{fileName}</span>
              <button
                type="button"
                onClick={handleClear}
                className="p-1 hover:bg-accent-2 rounded"
              >
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            </div>
          ) : (
            <div className="text-center">
              <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                Click to upload or drag and drop
              </p>
              {accept && (
                <p className="text-xs text-muted-foreground mt-1">
                  {accept}
                </p>
              )}
              {maxSize && (
                <p className="text-xs text-muted-foreground">
                  Max size: {(maxSize / 1024 / 1024).toFixed(0)}MB
                </p>
              )}
            </div>
          )}
        </div>
        {helperText && (
          <p className={cn('text-sm', error ? 'text-danger' : 'text-muted-foreground')}>
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

InputFile.displayName = 'InputFile';
